import { Document } from "@/lib/supabase";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { format } from "date-fns";

interface ViewDocumentDialogProps {
  document: Document;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ViewDocumentDialog({
  document,
  open,
  onOpenChange,
}: ViewDocumentDialogProps) {
  const formatBytes = (bytes: number | null) => {
    if (!bytes) return "N/A";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{document.title || "Untitled Document"}</DialogTitle>
          <DialogDescription>Document details and metadata</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-foreground">URL</label>
            <a
              href={document.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline mt-1"
            >
              {document.url}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {document.description && (
            <div>
              <label className="text-sm font-semibold text-foreground">Description</label>
              <p className="mt-1 text-muted-foreground">{document.description}</p>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-foreground">Tags</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {document.tags.length > 0 ? (
                document.tags.map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground text-sm">No tags</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-foreground">Hash</label>
              <p className="mt-1 text-muted-foreground font-mono text-sm">{document.hash}</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-foreground">Size</label>
              <p className="mt-1 text-muted-foreground">{formatBytes(document.size)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-foreground">Created</label>
              <p className="mt-1 text-muted-foreground text-sm">
                {format(new Date(document.created_at), "PPpp")}
              </p>
            </div>
            <div>
              <label className="text-sm font-semibold text-foreground">Updated</label>
              <p className="mt-1 text-muted-foreground text-sm">
                {format(new Date(document.updated_at), "PPpp")}
              </p>
            </div>
          </div>

          {document.content && (
            <div>
              <label className="text-sm font-semibold text-foreground">Content Preview</label>
              <div className="mt-1 max-h-64 overflow-y-auto rounded-md border bg-muted p-4">
                <pre className="whitespace-pre-wrap text-xs text-muted-foreground">
                  {document.content.substring(0, 1000)}
                  {document.content.length > 1000 && "..."}
                </pre>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
